import { type MutableRefObject } from 'react';

import { Button, Input, Select, Text, Textarea, type TypeSelectOption } from '@thefarmersfront/kitchen';

import { FormField, ModalSection } from '~/components/common';

import { useKakaoPostcodeSearch } from '~/hooks/common';
import { useClaimStore } from '~/store';
import { validateNumberAndHyphenOnly } from '~/utils';

import { DEFAULT_INPUT_STATUS } from '~/constants/common';

import type {
  TypeExchangeRegisterSecondStepErrorFieldRefs,
  TypeExchangeRegisterSecondStepValues,
  TypeExchangeRegisterSecondStepValuesState,
} from '~/types/claim.types';

import styles from '../ClaimExchangeRegisterSecondStepContent.module.scss';

const ClaimExchangeRegisterSecondStepExchangeDeliveryInfo = ({
  data,
  dataState,
  onChange,
  onChangeState,
  errorFieldRefs,
}: {
  data: TypeExchangeRegisterSecondStepValues['exchangeDeliveryInfo'];
  dataState: TypeExchangeRegisterSecondStepValuesState['exchangeDeliveryInfo'];
  onChange: (values: Partial<TypeExchangeRegisterSecondStepValues>) => void;
  onChangeState: (values: Partial<TypeExchangeRegisterSecondStepValuesState>) => void;
  errorFieldRefs: MutableRefObject<TypeExchangeRegisterSecondStepErrorFieldRefs>;
}) => {
  const { handleZipCodeSearchOpen } = useKakaoPostcodeSearch({
    onComplete: (_, address) =>
      onChange({
        exchangeDeliveryInfo: {
          ...data,
          primaryAddress: address,
          detailAddress: '',
          receivePlace: selectOptions.returnPlace[0],
          receivePlaceDetail: '',
          accessMethod: selectOptions.accessMethod[0],
          accessDetail: '',
        },
      }),
  });

  const selectOptions = useClaimStore((state) => state.selectOptions);

  const options = {
    returnPlace: selectOptions.returnPlace,
    accessMethod: selectOptions.accessMethod,
  };

  return (
    <ModalSection className={styles['exchange-delivery-info']} title="교환 배송 정보">
      <FormField
        label="수령자명"
        required
        contentAlign="center"
        contents={<Text size="large">{data.receiverName}</Text>}
      />
      <FormField
        label="연락처"
        required
        contents={
          <Input
            ref={(el) => (errorFieldRefs.current['exchangeDeliveryInfo.receiverContactNumber'] = el)}
            type="text"
            placeholder="- 포함 입력"
            size="small"
            maxWidth="148px"
            status={dataState.receiverContactNumber.status}
            validationMessage={dataState.receiverContactNumber.message}
            value={data.receiverContactNumber}
            onChange={(e) => {
              const newValue = e.target.value;
              if (validateNumberAndHyphenOnly(newValue)) {
                onChange({ exchangeDeliveryInfo: { ...data, receiverContactNumber: newValue } });
                onChangeState({ exchangeDeliveryInfo: { ...dataState, receiverContactNumber: DEFAULT_INPUT_STATUS } });
              }
            }}
            onBlur={(e) =>
              onChange({ exchangeDeliveryInfo: { ...data, receiverContactNumber: e.target.value.trim() } })
            }
            onClick={() =>
              onChangeState({ exchangeDeliveryInfo: { ...dataState, receiverContactNumber: DEFAULT_INPUT_STATUS } })
            }
          />
        }
      />
      <FormField
        label="기본주소"
        required
        contents={
          <>
            <Input
              ref={(el) => (errorFieldRefs.current['exchangeDeliveryInfo.primaryAddress'] = el)}
              type="text"
              size="small"
              disabled
              status={dataState.primaryAddress.status}
              validationMessage={dataState.primaryAddress.message}
              value={data.primaryAddress}
            />
            <Button
              size="small"
              color="secondary"
              onClick={() => {
                handleZipCodeSearchOpen();
                onChangeState({ exchangeDeliveryInfo: { ...dataState, primaryAddress: DEFAULT_INPUT_STATUS } });
              }}
            >
              검색
            </Button>
          </>
        }
        contentsClassName={styles['address-container']}
      />
      <FormField
        label="상세주소"
        contents={
          <Input
            type="text"
            placeholder="입력"
            size="small"
            maxLength={30}
            count
            value={data.detailAddress}
            onChange={(e) => onChange({ exchangeDeliveryInfo: { ...data, detailAddress: e.target.value } })}
            onBlur={(e) => onChange({ exchangeDeliveryInfo: { ...data, detailAddress: e.target.value.trim() } })}
          />
        }
      />
      <FormField
        label="수령장소"
        required
        contents={
          <Select
            ref={(el) => (errorFieldRefs.current['exchangeDeliveryInfo.receivePlace'] = el)}
            placeholder="선택"
            size="small"
            maxWidth="148px"
            options={options.returnPlace}
            status={dataState.receivePlace.status}
            validationMessage={dataState.receivePlace.message}
            value={data.receivePlace}
            onChange={(_: unknown, value: TypeSelectOption<string>) =>
              onChange({ exchangeDeliveryInfo: { ...data, receivePlace: value } })
            }
            onClick={() =>
              onChangeState({ exchangeDeliveryInfo: { ...dataState, receivePlace: DEFAULT_INPUT_STATUS } })
            }
          />
        }
      />
      <FormField
        label="수령장소 상세"
        contents={
          <Textarea
            placeholder="입력"
            size="small"
            count
            maxLength={100}
            value={data.receivePlaceDetail}
            onChange={(e) => onChange({ exchangeDeliveryInfo: { ...data, receivePlaceDetail: e.target.value } })}
            onBlur={(e) => onChange({ exchangeDeliveryInfo: { ...data, receivePlaceDetail: e.target.value.trim() } })}
          />
        }
      />
      <FormField
        label="출입방법"
        required
        contents={
          <Select
            ref={(el) => (errorFieldRefs.current['exchangeDeliveryInfo.accessMethod'] = el)}
            placeholder="선택"
            size="small"
            maxWidth="148px"
            options={options.accessMethod}
            status={dataState.accessMethod.status}
            validationMessage={dataState.accessMethod.message}
            value={data.accessMethod}
            onChange={(_: unknown, value: TypeSelectOption<string>) =>
              onChange({ exchangeDeliveryInfo: { ...data, accessMethod: value } })
            }
            onClick={() =>
              onChangeState({ exchangeDeliveryInfo: { ...dataState, accessMethod: DEFAULT_INPUT_STATUS } })
            }
          />
        }
      />
      <FormField
        label="출입방법 상세"
        contents={
          <Textarea
            placeholder="입력"
            size="small"
            count
            maxLength={100}
            value={data.accessDetail}
            onChange={(e) => onChange({ exchangeDeliveryInfo: { ...data, accessDetail: e.target.value } })}
            onBlur={(e) => onChange({ exchangeDeliveryInfo: { ...data, accessDetail: e.target.value.trim() } })}
          />
        }
      />
    </ModalSection>
  );
};

export default ClaimExchangeRegisterSecondStepExchangeDeliveryInfo;
